import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

const RailOsDropHighlightContext = createContext<string | null>(null);

/** Active OS file-drop target id (`data-rail-drop-id`) under the pointer. */
export function useRailOsDropHighlightId(): string | null {
  return useContext(RailOsDropHighlightContext);
}

export function RailFilesDropHighlightProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [osActiveDropId, setOsActiveDropId] = useState<string | null>(null);

  useEffect(() => {
    const clear = () => setOsActiveDropId(null);

    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes("Files")) return;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const zone = el?.closest("[data-rail-drop-id]");
      setOsActiveDropId(zone?.getAttribute("data-rail-drop-id") ?? null);
    };

    // Bubble phase at the document — every real drop zone handles + stops
    // propagation before this fires, so it only sees OS file drags nothing
    // claimed. preventDefault stops the browser from navigating to (i.e.
    // opening/downloading) a file dropped on a non-zone spot like a section
    // header or workspace row.
    const preventUnhandled = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes("Files")) return;
      e.preventDefault();
    };

    // Capture phase — child zones call stopPropagation on dragOver.
    document.addEventListener("dragover", onDragOver, true);
    document.addEventListener("drop", clear, true);
    document.addEventListener("dragend", clear);
    document.addEventListener("dragover", preventUnhandled);
    document.addEventListener("drop", preventUnhandled);
    return () => {
      document.removeEventListener("dragover", onDragOver, true);
      document.removeEventListener("drop", clear, true);
      document.removeEventListener("dragend", clear);
      document.removeEventListener("dragover", preventUnhandled);
      document.removeEventListener("drop", preventUnhandled);
    };
  }, []);

  return (
    <RailOsDropHighlightContext.Provider value={osActiveDropId}>
      {children}
    </RailOsDropHighlightContext.Provider>
  );
}
